/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { DrawingPinIcon, HomeIcon, IdCardIcon } from '@/shared/ui/radix-icons';
import { Text, TextInput } from '@/shared/ui';
import { getCategoryLabel, PROPERTY_CATEGORIES } from '@features/property-management/components/PropertyDrawer/schema';
import { ChipBtn, SectionHeader, type ClientFilters } from './ClientFilterPanel.parts';

/** The four canonical demand vocabularies, in the drawer's own order. */
const BUSINESS_TYPES = ['sale', 'rent', 'exchange', 'both', 'short_term'] as const;

/** Bedroom counts offered as chips, mirroring the property filter panel. */
const BEDROOM_CHOICES = ['1', '2', '3', '4', '5'] as const;

type Translate = (key: string) => string;

interface SectionProps {
  t: Translate;
  filters: ClientFilters;
  onChange: (patch: Partial<ClientFilters>) => void;
}

const fieldLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--text-meta)',
  color: 'var(--text-tertiary)',
  marginBottom: 'var(--space-1)',
};

/**
 * One labelled input inside a filter section.
 *
 * The label is the input's own, so clicking it focuses the field -- the
 * shared TextInput renders a real `<label for>` when given one.
 *
 * @param props - Field name, label, current value, change handler and hints.
 * @returns The rendered field.
 */
const Field: React.FC<{
  name: string;
  label: string;
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: 'text' | 'number';
  maxLength?: number;
}> = ({ name, label, value, onChange, placeholder, type = 'text', maxLength }) => (
  <div style={{ flex: 1, minWidth: 0 }}>
    <TextInput
      name={`client-filter-${name}`}
      label={label}
      type={type}
      value={value ?? ''}
      placeholder={placeholder}
      maxLength={maxLength}
      onChange={(event) => onChange(event.target.value)}
    />
  </div>
);

/** Two fields side by side, for the min/max pairs. */
const FieldRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: 'flex', gap: 'var(--space-2)' }}>{children}</div>
);

/**
 * Monthly-income window.
 *
 * A client with no income on file matches neither bound -- the backend is
 * explicit about it -- so this narrows to the records a broker actually
 * filled in rather than quietly including the blank ones.
 *
 * @param props - Translator, current filters and the change handler.
 * @returns The rendered income section.
 */
export const IncomeSection: React.FC<SectionProps> = ({ t, filters, onChange }) => (
  <div>
    <SectionHeader icon={<IdCardIcon width={14} height={14} />} title={t('filterPanel.income')} />
    <FieldRow>
      <Field
        name="incomeMin"
        label={t('filterPanel.incomeMin')}
        value={filters.incomeMin}
        onChange={(incomeMin) => onChange({ incomeMin })}
        placeholder="0"
        type="number"
      />
      <Field
        name="incomeMax"
        label={t('filterPanel.incomeMax')}
        value={filters.incomeMax}
        onChange={(incomeMax) => onChange({ incomeMax })}
        placeholder="0"
        type="number"
      />
    </FieldRow>
  </div>
);

/**
 * Where the client lives.
 *
 * @param props - Translator, current filters and the change handler.
 * @returns The rendered address section.
 */
export const AddressSection: React.FC<SectionProps> = ({ t, filters, onChange }) => (
  <div>
    <SectionHeader icon={<DrawingPinIcon width={14} height={14} />} title={t('filterPanel.address')} />
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      <Field
        name="city"
        label={t('filterPanel.city')}
        value={filters.city}
        onChange={(city) => onChange({ city })}
        placeholder={t('filterPanel.cityPlaceholder')}
      />
      <FieldRow>
        <Field
          name="neighborhood"
          label={t('filterPanel.neighborhood')}
          value={filters.neighborhood}
          onChange={(neighborhood) => onChange({ neighborhood })}
        />
        <Field
          name="state"
          label={t('filterPanel.state')}
          value={filters.state}
          onChange={(state) => onChange({ state })}
          placeholder="SP"
          maxLength={2}
        />
      </FieldRow>
    </div>
  </div>
);

/**
 * What the client is looking for, read from the demand profile.
 *
 * Deliberately separate from the address section above: a client living in
 * Sorocaba and hunting in Campinas belongs to one of the two answers, not
 * to both.
 *
 * @param props - Translator, current filters and the change handler.
 * @returns The rendered preference section.
 */
export const PreferenceSection: React.FC<SectionProps> = ({ t, filters, onChange }) => (
  <div>
    <SectionHeader icon={<HomeIcon width={14} height={14} />} title={t('filterPanel.preferences')} />
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <FieldRow>
        <Field
          name="prefCity"
          label={t('filterPanel.prefCity')}
          value={filters.prefCity}
          onChange={(prefCity) => onChange({ prefCity })}
        />
        <Field
          name="prefNeighborhood"
          label={t('filterPanel.prefNeighborhood')}
          value={filters.prefNeighborhood}
          onChange={(prefNeighborhood) => onChange({ prefNeighborhood })}
        />
      </FieldRow>

      <FieldRow>
        <Field
          name="prefPriceMin"
          label={t('filterPanel.prefPriceMin')}
          value={filters.prefPriceMin}
          onChange={(prefPriceMin) => onChange({ prefPriceMin })}
          placeholder="0"
          type="number"
        />
        <Field
          name="prefPriceMax"
          label={t('filterPanel.prefPriceMax')}
          value={filters.prefPriceMax}
          onChange={(prefPriceMax) => onChange({ prefPriceMax })}
          placeholder="0"
          type="number"
        />
      </FieldRow>

      <div>
        <Text component="span" style={fieldLabelStyle}>{t('filterPanel.prefBusinessType')}</Text>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
          {BUSINESS_TYPES.map((value) => (
            <ChipBtn
              key={value}
              value={t(`form.businessTypes.${value}`)}
              active={filters.prefBusinessType === value}
              onClick={() => onChange({
                prefBusinessType: filters.prefBusinessType === value ? '' : value,
              })}
            />
          ))}
        </div>
      </div>

      <div>
        <Text component="span" style={fieldLabelStyle}>{t('filterPanel.prefPropertyKind')}</Text>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'var(--space-2)',
            maxHeight: '8rem',
            overflowY: 'auto',
            paddingRight: 'var(--space-1)',
          }}
        >
          {PROPERTY_CATEGORIES.map((category) => (
            <ChipBtn
              key={category}
              value={getCategoryLabel(category)}
              active={filters.prefPropertyKind === category}
              onClick={() => onChange({
                prefPropertyKind: filters.prefPropertyKind === category ? '' : category,
              })}
            />
          ))}
        </div>
      </div>

      <div>
        <Text component="span" style={fieldLabelStyle}>{t('filterPanel.prefBedroomsMin')}</Text>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
          {BEDROOM_CHOICES.map((count) => (
            <ChipBtn
              key={count}
              value={`${count}+`}
              active={filters.prefBedroomsMin === count}
              onClick={() => onChange({
                prefBedroomsMin: filters.prefBedroomsMin === count ? '' : count,
              })}
            />
          ))}
          <ChipBtn
            value={t('filterPanel.petFriendly')}
            active={!!filters.prefPetFriendly}
            onClick={() => onChange({ prefPetFriendly: !filters.prefPetFriendly })}
          />
        </div>
      </div>
    </div>
  </div>
);
